import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { createContext, useState, useReducer, useContext, useRef, useEffect } from "react";
import FocusLock from "react-focus-lock";
import { ariaHideOthers } from "./ariaHideOthers.mjs";
import { Box } from "../../Box/Box.mjs";
import { ThemePortal } from "../../ThemePortal/ThemePortal.mjs";
import { useDialogController } from "../../useDialogController/useDialogController.mjs";
import { externalGutter } from "./ModalExternalGutter.mjs";
import { ModalContent } from "./ModalContent.mjs";
import { resetStackingContext, backdrop, horiztontalTransition, modalContainer, entrance, exit, fixedStackingContext } from "./Modal.css.mjs";
import { WarningModal } from "./WarningModal.mjs";
import { useModalContext } from "../../../hooks/useModalContext/index.mjs";
import { PreventViewportZoomIn } from "./PreventViewportZoomIn.mjs";
const AllowCloseContext = createContext(true);
const ModalPortal = ({ children }) => {
  const [modalElement, setElement] = useState(null);
  useEffect(() => {
    const modalContainerId = "swift-modal-container";
    let element = document.getElementById(modalContainerId);
    if (!element) {
      element = document.createElement("div");
      element.setAttribute("id", modalContainerId);
      element.setAttribute("class", fixedStackingContext);
      document.body.appendChild(element);
    }
    setElement(element);
  }, []);
  if (!modalElement) {
    return null;
  }
  return /* @__PURE__ */ jsx(ThemePortal, { container: modalElement, children });
};
const OPEN_MODAL = 1;
const CLOSE_MODAL = 2;
const ANIMATION_COMPLETE = 3;
const INITIAL = 1;
const OPEN = 2;
const OPENING = 3;
const CLOSED = 4;
const CLOSING = 5;
const reducer = (prevState, action) => {
  switch (action) {
    case OPEN_MODAL: {
      switch (prevState) {
        case INITIAL:
        case CLOSING:
        case CLOSED: {
          return OPENING;
        }
        default:
          return prevState;
      }
    }
    case CLOSE_MODAL: {
      switch (prevState) {
        case OPEN:
        case OPENING: {
          return CLOSING;
        }
        default:
          return prevState;
      }
    }
    case ANIMATION_COMPLETE: {
      switch (prevState) {
        case CLOSING: {
          return CLOSED;
        }
        case OPENING: {
          return OPEN;
        }
        default:
          return prevState;
      }
    }
  }
};
const ANIMATION_DURATION = 300;
const Modal = ({
  id,
  open,
  children,
  description,
  onClose,
  width,
  closeLabel,
  illustration,
  title,
  headingLevel,
  position,
  data,
  background = "surface",
  hideXButton = false,
  headerIllustration,
  warningModal,
  actionButton
}) => {
  const [trapActive, setTrapActive] = useState(true);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const allowClose = useContext(AllowCloseContext);
  const { showCloseWarning } = useModalContext();
  const { visible, showModal, closeModal } = useDialogController(false);
  const closeModalId = `${id}-warning-modal`;
  const closeModalSubmit = () => dispatch(CLOSE_MODAL);
  const shouldFocus = typeof document !== "undefined" && typeof document.hasFocus === "function" && document.hasFocus();
  const openRef = useRef(open);
  const modalRef = useRef(null);
  const headingRef = useRef(null);
  const closeHandlerRef = useRef(onClose);
  const initiateClose = () => {
    if (showCloseWarning && Boolean(warningModal)) {
      showModal();
    } else if (allowClose) {
      dispatch(CLOSE_MODAL);
    }
  };
  useEffect(() => {
    openRef.current = open;
    dispatch(open ? OPEN_MODAL : CLOSE_MODAL);
  }, [open]);
  useEffect(() => {
    if (state === CLOSING) {
      const timer = setTimeout(() => {
        dispatch(ANIMATION_COMPLETE);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
    if (state === CLOSED && openRef.current) {
      closeHandlerRef.current(false);
    }
  }, [state]);
  const shouldAriaHideOthers = state === OPEN || state === CLOSING;
  useEffect(() => {
    if (shouldAriaHideOthers && modalRef.current) {
      return ariaHideOthers(modalRef.current, { delay: ANIMATION_DURATION });
    }
  }, [shouldAriaHideOthers]);
  useEffect(() => {
    if (typeof onClose === "function") {
      closeHandlerRef.current = onClose;
    }
  }, [onClose]);
  useEffect(() => {
    const event = trapActive ? "blur" : "focus";
    const handleEvent = () => setTrapActive(!trapActive);
    window.addEventListener(event, handleEvent);
    return () => {
      window.removeEventListener(event, handleEvent);
    };
  }, [trapActive]);
  return state === OPENING || state === OPEN || state === CLOSING ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ModalPortal, { children: /* @__PURE__ */ jsxs(
      FocusLock,
      {
        className: resetStackingContext,
        disabled: !trapActive,
        autoFocus: false,
        onActivation: () => {
          if (state === OPEN) {
            return;
          }
          if (headingRef.current && shouldFocus) {
            headingRef.current.focus();
          }
          dispatch(ANIMATION_COMPLETE);
        },
        returnFocus: true,
        children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              onClick: state === OPEN && !hideXButton ? initiateClose : void 0,
              position: "fixed",
              inset: 0,
              zIndex: "modalBackdrop",
              transition: position === "center" ? "fast" : void 0,
              opacity: state !== OPEN ? 0 : void 0,
              pointerEvents: state === CLOSING ? "none" : void 0,
              className: [
                backdrop,
                position === "left" || position === "right" && horiztontalTransition
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              position: "fixed",
              inset: 0,
              zIndex: "modal",
              pointerEvents: "none",
              transition: "fast",
              opacity: state !== OPEN ? 0 : void 0,
              paddingLeft: position === "right" ? ["none", "xlarge"] : void 0,
              paddingRight: position === "left" ? ["none", "xlarge"] : void 0,
              padding: position === "center" ? externalGutter : void 0,
              className: [
                modalContainer,
                position === "left" || position === "right" && horiztontalTransition,
                state === OPENING && entrance[position],
                state === CLOSING && position in exit && exit[position]
              ],
              height: "full",
              children: /* @__PURE__ */ jsx(
                ModalContent,
                {
                  id,
                  description,
                  onClose: initiateClose,
                  width,
                  closeLabel,
                  illustration,
                  title,
                  headingLevel,
                  headingRef,
                  modalRef,
                  position,
                  scrollLock: !(state === CLOSING),
                  data,
                  background,
                  hideXButton,
                  headerIllustration,
                  actionButton,
                  children
                }
              )
            }
          )
        ]
      }
    ) }),
    warningModal && /* @__PURE__ */ jsx(
      WarningModal,
      {
        visible,
        closeModal,
        title: warningModal.title,
        description: warningModal.description,
        closeButtonLabel: warningModal == null ? void 0 : warningModal.closeButtonLabel,
        confirmButtonLabel: warningModal.confirmButtonLabel,
        id: closeModalId,
        onSubmit: closeModalSubmit
      }
    ),
    /* @__PURE__ */ jsx(PreventViewportZoomIn, {})
  ] }) : null;
};
export {
  AllowCloseContext,
  Modal
};
